import { addDays, endOfDay, startOfDay } from "date-fns";

export interface FollowUpMaxDays {
  /** Furthest a follow-up may be dated, in days from today; null = no cap. */
  maxDays: number | null;
  /** Which pipeline level the cap came from. */
  from: "sub-status" | "status" | "stage" | null;
}

const NO_CAP: FollowUpMaxDays = Object.freeze({ maxDays: null, from: null });
const NO_DUE: FollowUpDueDays = Object.freeze({ days: null, from: null });

export interface FollowUpDueDays {
  /** Configured "Due in N days" for the bucket; null = nothing configured. */
  days: number | null;
  /** Which pipeline level the value came from. */
  from: "sub-status" | "status" | "stage" | null;
}

/** Loose pipeline shapes — the API response is untyped upstream. */
interface PipelineSubStatusLike {
  id?: number | string;
  maxFollowUpDays?: unknown;
  followUpDays?: unknown;
}
interface PipelineStatusLike extends PipelineSubStatusLike {
  subStatuses?: PipelineSubStatusLike[];
}
interface PipelineStageLike extends PipelineSubStatusLike {
  statuses?: PipelineStatusLike[];
  statusList?: PipelineStatusLike[];
}
interface EntityLike {
  stageId?: number | string | null;
  statusId?: number | string | null;
  subStatusId?: number | string | null;
  stage?: { id?: number | string | null } | null;
  status?: { id?: number | string | null } | null;
  subStatus?: { id?: number | string | null } | null;
}

const clampDays = (raw: unknown): number | null => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
};

/**
 * Maximum follow-up horizon for an entity (lead or deal), resolved from the
 * pipeline the same way the server does (`followup-policy.ts`): the entity's
 * sub-status → status → stage, most specific non-null `maxFollowUpDays` wins,
 * nothing set = no cap.
 *
 * Built once per pipeline (three id → value maps) and applied per entity, the
 * same shape as `buildEntityRejectionResolver`. The drawer's calendar greys
 * out the days past the cap and says why; the server is the real gate and
 * rejects a later date with a 400 even if a client skips this.
 */
export function buildFollowUpMaxDaysResolver(
  pipeline: PipelineStageLike[] | null | undefined,
): (entity: EntityLike | null | undefined) => FollowUpMaxDays {
  const byStageId = new Map<number, number | null>();
  const byStatusId = new Map<number, number | null>();
  const bySubStatusId = new Map<number, number | null>();
  for (const stg of pipeline ?? []) {
    byStageId.set(Number(stg?.id), clampDays(stg?.maxFollowUpDays));
    const statuses = stg?.statuses || stg?.statusList || [];
    for (const st of statuses) {
      byStatusId.set(Number(st?.id), clampDays(st?.maxFollowUpDays));
      for (const ss of st?.subStatuses || []) {
        bySubStatusId.set(Number(ss?.id), clampDays(ss?.maxFollowUpDays));
      }
    }
  }
  return (entity): FollowUpMaxDays => {
    if (!entity) return NO_CAP;
    const subStatusId = entity?.subStatus?.id ?? entity?.subStatusId;
    const statusId = entity?.status?.id ?? entity?.statusId;
    const stageId = entity?.stage?.id ?? entity?.stageId;

    const subMax =
      subStatusId != null ? bySubStatusId.get(Number(subStatusId)) ?? null : null;
    if (subMax != null) return { maxDays: subMax, from: "sub-status" };
    const statusMax =
      statusId != null ? byStatusId.get(Number(statusId)) ?? null : null;
    if (statusMax != null) return { maxDays: statusMax, from: "status" };
    const stageMax = stageId != null ? byStageId.get(Number(stageId)) ?? null : null;
    if (stageMax != null) return { maxDays: stageMax, from: "stage" };
    return NO_CAP;
  };
}

/**
 * The last selectable day under a cap: end of `today + maxDays` in the
 * browser's local day (IST for every Xale user). Mirrors the server's
 * `followUpMaxDueAt`, which is end-of-day IST inclusive.
 */
export function followUpMaxDate(
  maxDays: number | null,
  now: Date = new Date(),
): Date | null {
  if (maxDays == null) return null;
  return endOfDay(addDays(startOfDay(now), Math.max(0, Math.floor(maxDays))));
}

/**
 * DEFAULT FOLLOW-UP HORIZON for a bucket (stage editor "Default follow-up" /
 * `followUpDays`), resolved exactly like the server's `resolveFollowUpPolicy`:
 * sub-status → status → stage, most specific non-null wins.
 *
 * Unlike the server — which falls back to 0 ("same day") because it always has
 * to date a row — this returns `null` when NOTHING is configured, so a caller
 * can tell "this bucket says same day" from "this bucket says nothing". The
 * drawer uses that to leave an existing follow-up date alone instead of
 * yanking it to today for a pipeline that never opted in.
 */
export function buildFollowUpDaysResolver(
  pipeline: PipelineStageLike[] | null | undefined,
): (entity: EntityLike | null | undefined) => FollowUpDueDays {
  const byStageId = new Map<number, number | null>();
  const byStatusId = new Map<number, number | null>();
  const bySubStatusId = new Map<number, number | null>();
  for (const stg of pipeline ?? []) {
    byStageId.set(Number(stg?.id), clampDays(stg?.followUpDays));
    const statuses = stg?.statuses || stg?.statusList || [];
    for (const st of statuses) {
      byStatusId.set(Number(st?.id), clampDays(st?.followUpDays));
      for (const ss of st?.subStatuses || []) {
        bySubStatusId.set(Number(ss?.id), clampDays(ss?.followUpDays));
      }
    }
  }
  return (entity): FollowUpDueDays => {
    if (!entity) return NO_DUE;
    const subStatusId = entity?.subStatus?.id ?? entity?.subStatusId;
    const statusId = entity?.status?.id ?? entity?.statusId;
    const stageId = entity?.stage?.id ?? entity?.stageId;

    const subDays =
      subStatusId != null ? bySubStatusId.get(Number(subStatusId)) ?? null : null;
    if (subDays != null) return { days: subDays, from: "sub-status" };
    const statusDays =
      statusId != null ? byStatusId.get(Number(statusId)) ?? null : null;
    if (statusDays != null) return { days: statusDays, from: "status" };
    const stageDays =
      stageId != null ? byStageId.get(Number(stageId)) ?? null : null;
    if (stageDays != null) return { days: stageDays, from: "stage" };
    return NO_DUE;
  };
}

/** The day a bucket's default follow-up lands on: start of `today + days`. */
export function followUpDefaultDate(
  days: number | null,
  now: Date = new Date(),
): Date | null {
  if (days == null) return null;
  return addDays(startOfDay(now), Math.max(0, Math.floor(days)));
}
